import React from 'react';
import throttle from 'lodash/throttle';
import ddt from '@wix/da-ddt';

const logger = ddt.createLogger('onscrollbottom');

export enum DISTANCE_UNIT {
  PX = 'PX',
  WINDOW_HEIGHT = 'WINDOW_HEIGHT',
}

const DEFAULT_THROTTLE_TIMEOUT = 250;

// !!!
// This component is only useful for tracking window/container planted on INITIAL RENDER
// dynamically added scrollable areas do not work nicely with its context approach
// requiring to re-render it once the scrollable area mounted to pass the received nodeRef to this component via context

// For dynamically added scrollable areas use ScrollableContent

export interface Props {
  onScrolledToBottom?(scrollDelta: number): void;
  // trigger onScrolledToBottom() when distance to bottom is equal or less than:
  distanceToBottom?: number;
  distanceUnit?: DISTANCE_UNIT;
  // should we check if it's already at the bottom when this mounts?
  checkOnMount?: boolean;
  // should we check if it's already at the bottom on every update?
  checkOnUpdate?: boolean;
  /** ms timeout for throttling onScrolledToBottom */
  timeout?: number;
  children?: React.ReactElement<any>;
}

class OnScrollBottom extends React.Component<Props> {
  private prevScrollTop;

  childNodeRef: React.RefObject<any> = React.createRef();

  componentDidMount() {
    window && window.addEventListener('scroll', this.callOnScrolledToBottom);
    if (this.props.checkOnMount) {
      logger.log('checking distance to bottom on mount');
      this.callOnScrolledToBottom();
    }
  }

  componentDidUpdate() {
    if (this.props.checkOnUpdate) {
      logger.log('checking distance to bottom on update');
      this.callOnScrolledToBottom();
    }
  }

  componentWillUnmount() {
    window && window.removeEventListener('scroll', this.callOnScrolledToBottom);
  }

  render() {
    const { children } = this.props;
    if (!children) {
      return null;
    }
    return React.cloneElement(children, { ref: this.childNodeRef });
  }

  getDistanceToBottomWhenShouldTrigger() {
    const { distanceToBottom, distanceUnit } = this.props;
    if (distanceUnit === DISTANCE_UNIT.PX) {
      return distanceToBottom!;
    }

    return distanceToBottom! * window.innerHeight!;
  }

  getDistanceToBottom() {
    const { children } = this.props;

    if (children) {
      const childNode = this.childNodeRef.current;
      if (!childNode) {
        return 0;
      }

      // this child bottom position is relative to the top of the view port
      const childBottom = childNode.getBoundingClientRect().bottom;
      // we are interestedin the distance from the bottom of the view port to the child bottom
      const distanceToChildBottom = childBottom - window.innerHeight;
      return distanceToChildBottom;
    }

    const distanceToScrollContainerBottom =
      window.document.body.scrollHeight - window.scrollY - window.innerHeight;
    logger.log(
      'window.document.body.scrollHeight',
      window.document.body.scrollHeight,
      'window.scrollY',
      window.scrollY,
      'window.innerHeight',
      window.innerHeight,
      'distanceToScrollContainerBottom',
      distanceToScrollContainerBottom
    );

    return distanceToScrollContainerBottom;
  }

  rememberScrollTop = () => {
    this.prevScrollTop = window.scrollY;
  };

  callOnScrolledToBottom = throttle(
    () =>
      requestAnimationFrame(() => {
        const { onScrolledToBottom } = this.props;
        if (!onScrolledToBottom) {
          return;
        }
        if (!window.document.body.scrollHeight) {
          return;
        }

        const distance = this.getDistanceToBottom();
        const threshold = this.getDistanceToBottomWhenShouldTrigger();
        if (distance <= threshold) {
          logger.log(`distance to bottom ${distance} within ${threshold}`);
          const scrollDelta = window.scrollY - this.prevScrollTop || 0;
          onScrolledToBottom(scrollDelta);
        }

        this.rememberScrollTop();
      }),
    this.props.timeout ?? DEFAULT_THROTTLE_TIMEOUT
  );
}

export default OnScrollBottom;
